import type { CreditAllowanceNudgeThreshold } from '@heidi/commerce';

export type AgentCreditAllowanceBannerSurface =
  | {
      kind: 'upgrade';
      threshold: CreditAllowanceNudgeThreshold;
      dismissible: boolean;
    }
  | {
      kind: 'complimentary';
      threshold: 100;
      dismissible: true;
    };

export function resolveAgentCreditAllowanceBanner({
  isOptInCreditUsageEnabled,
  isMaxSeat,
  threshold,
  isConsumed,
  forceExhausted = false,
}: {
  isOptInCreditUsageEnabled: boolean;
  isMaxSeat: boolean;
  threshold: CreditAllowanceNudgeThreshold | null;
  isConsumed: boolean;
  /** 402 just landed — paint 100 even if GET still shows remaining > 0. */
  forceExhausted?: boolean;
}): AgentCreditAllowanceBannerSurface | null {
  if (!isOptInCreditUsageEnabled) {
    return null;
  }

  const resolvedThreshold: CreditAllowanceNudgeThreshold | null = forceExhausted
    ? 100
    : threshold;
  if (resolvedThreshold == null) {
    return null;
  }

  if (isMaxSeat) {
    if (resolvedThreshold !== 100 || isConsumed) {
      return null;
    }
    return { kind: 'complimentary', threshold: 100, dismissible: true };
  }

  if (resolvedThreshold === 100) {
    return { kind: 'upgrade', threshold: 100, dismissible: false };
  }

  if (isConsumed) {
    return null;
  }

  return { kind: 'upgrade', threshold: resolvedThreshold, dismissible: true };
}

/**
 * Standard seat at remaining <= 0 (GET 100 or a 402 hold). A Max seat's
 * complimentary overage (Clinician Max or Practice Max) stays sendable. Used to grey Send and skip create/turn POSTs.
 */
export function shouldSkipCreditAllowanceSend({
  isOptInCreditUsageEnabled,
  isGateEnabled,
  isMaxSeat,
  threshold,
  forceExhausted = false,
}: {
  isOptInCreditUsageEnabled: boolean;
  isGateEnabled: boolean;
  isMaxSeat: boolean;
  threshold: CreditAllowanceNudgeThreshold | null;
  forceExhausted?: boolean;
}): boolean {
  return (
    isOptInCreditUsageEnabled &&
    isGateEnabled &&
    !isMaxSeat &&
    (forceExhausted || threshold === 100)
  );
}

export type AgentCreditAllowanceBannerPlacement = 'composer' | 'transcript';

/** Landing keeps 100 on the dock; a bound chat moves 100 under the last message. */
export function placeAgentCreditAllowanceBanner({
  surface,
  inThread,
}: {
  surface: AgentCreditAllowanceBannerSurface | null;
  inThread: boolean;
}): AgentCreditAllowanceBannerPlacement | null {
  if (surface == null) {
    return null;
  }
  if (inThread && surface.threshold === 100) {
    return 'transcript';
  }
  return 'composer';
}

/** Hide in-chat follow-ups only when a Standard seat (not Max) is at remaining <= 0. */
export function shouldHideFollowUpQuestionsWhenCreditExhausted({
  isOptInCreditUsageEnabled,
  isGateEnabled,
  isMaxSeat,
  threshold,
  forceExhausted = false,
}: {
  isOptInCreditUsageEnabled: boolean;
  isGateEnabled: boolean;
  isMaxSeat: boolean;
  threshold: CreditAllowanceNudgeThreshold | null;
  forceExhausted?: boolean;
}): boolean {
  return shouldSkipCreditAllowanceSend({
    isOptInCreditUsageEnabled,
    isGateEnabled,
    isMaxSeat,
    threshold,
    forceExhausted,
  });
}
